import { apiClient } from '@/lib/apiClient';

const BASE_PATH = '/api/assistant/settings';
const USAGE_PATH = '/api/assistant/usage';

export type AiSettingsScope = 'Host' | 'Tenant';

/** The providers the assistant can run on; each has its own model and key. */
export type AiProvider = 'Anthropic' | 'Berget';
export const AI_PROVIDERS: AiProvider[] = ['Anthropic', 'Berget'];

/** One provider's state in the caller's scope, after inheritance. Never carries the key. */
export interface AiProviderSettings {
  provider: AiProvider;
  model: string;
  apiKeySet: boolean;
}

/** One entry of a customer's ordered role → provider + model list. */
export interface AiRoleModel {
  role: string;
  provider: AiProvider;
  model: string;
}

/** What is in force for the caller's scope. The keys themselves never travel. */
export interface AiAssistantSettings {
  provider: AiProvider;
  model: string;
  apiKeySet: boolean;
  scope: AiSettingsScope;
  providers: AiProviderSettings[];
  /** True when the customer chooses the model per role; always false on the host's side. */
  modelPerRole?: boolean;
  /** The customer's ordered role list, kept while the switch is off. */
  roleModels?: AiRoleModel[];
  /** The names of the customer's roles, to list from; empty on the host's side. */
  roles?: string[];
}

export interface UpdateAiAssistantSettings {
  provider: AiProvider;
  model: string;
  /** A new key for the provider, or empty to keep the stored one. */
  apiKey?: string;
  /** Delete the provider's key stored for this scope; a tenant then inherits the host's. */
  clearApiKey?: boolean;
  /** Turn model per role on or off; absent to leave it. A customer's setting only. */
  modelPerRole?: boolean;
  /** The new ordered role list; absent to leave it. A customer's setting only. */
  roleModels?: AiRoleModel[];
}

export interface AiConnectionTestResult {
  succeeded: boolean;
  provider: AiProvider;
  model?: string;
  inputTokens: number;
  outputTokens: number;
  message?: string;
}

/** One model a provider publishes. */
export interface AiModelOption {
  id: string;
  name: string;
  toolCalling: boolean;
  available: boolean;
  currency?: string;
  inputPricePerMillionTokens?: number;
  outputPricePerMillionTokens?: number;
}

/** One user's turns on one model over the period, from the audit log. */
export interface AiUsageRow {
  userId?: string;
  userName?: string;
  provider: AiProvider;
  model: string;
  /** Questions answered. */
  turns: number;
  /** LLM calls those questions took, tool rounds included. */
  llmCalls: number;
  inputTokens: number;
  outputTokens: number;
  /** The priced turns' cost at list price; absent when no turn in the row was priced. */
  cost?: number;
  currency?: string;
  /** Turns whose model had no price at the time, so `cost` understates them. */
  unpricedTurns: number;
}

export interface AiUsageReport {
  from: string;
  to: string;
  tenantId?: string;
  rows: AiUsageRow[];
}

/** The assistant's provider, model and API key at runtime; every call takes `NexoAI.ManageSettings`. */
export const aiAssistantSettingsService = {
  async get(): Promise<AiAssistantSettings> {
    const { data } = await apiClient.get<AiAssistantSettings>(BASE_PATH);
    return data;
  },

  async update(input: UpdateAiAssistantSettings): Promise<AiAssistantSettings> {
    const { data } = await apiClient.put<AiAssistantSettings>(BASE_PATH, input);
    return data;
  },

  /** One short request with the saved settings. */
  async testConnection(): Promise<AiConnectionTestResult> {
    const { data } = await apiClient.post<AiConnectionTestResult>(`${BASE_PATH}/test`);
    return data;
  },

  /** The provider's public catalog; empty for a provider without one. */
  async getModels(provider: AiProvider): Promise<AiModelOption[]> {
    const { data } = await apiClient.get<AiModelOption[]>(`${BASE_PATH}/models`, { params: { provider } });
    return data;
  },

  /**
   * The caller's scope's usage between two dates (YYYY-MM-DD), both inclusive, by user, provider
   * and model.
   */
  async getUsage(from: string, to: string): Promise<AiUsageReport> {
    const { data } = await apiClient.get<AiUsageReport>(USAGE_PATH, { params: { from, to } });
    return data;
  },
};
